import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { DotMatrix } from '@/components/dot-matrix';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import data from '@/data/eyes-on-equality.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import is from '@sindresorhus/is';
import { InferGetServerSidePropsType } from 'next';
import { useMemo, useState } from 'react';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getMinorityLabel = (
  key: keyof EqualityForSomeData['equal_treatment_important_generation_gap']
): string =>
  t({
    id: 'eyes-on-equality.minorities',
    message: select(key, {
      minority_racial: 'Racial and ethnic minorities',
      minority_religious: 'Religious minorities',
      minority_females: 'Females',
      minority_lgbtq: 'LGBTQ+ people',
    }),
  });

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as EyesOnEqualityData }),
  getStoryProps('eyes-on-equality', 'equality-for-some')
);

export default function EyesOnEquality(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');

  const sharingImage = useSharingImage('eyes-on-equality');
  const { i18n } = useLingui();

  const pageTitle = t({ id: 'eyes-on-equality.title', message: 'Eyes on equality' });
  const pageDescription = t({
    id: 'eyes-on-equality.description',
    message:
      'Over the past several decades, many countries have passed laws aimed at enshrining equal rights for all. How have views across generations evolved?',
  });

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  const formatValue = useNumberFormatting('percent');

  const [chartData, setChartData] =
    useState<ValueOf<EyesOnEqualityData['equal_treatment_important_hic']>>();
  const [chartLabel, setChartLabel] = useState('');

  const answers = useMemo(
    () => [
      { id: 'answer1', label: youngerGeneration(i18n) },
      { id: 'answer2', label: olderGeneration(i18n) },
      {
        id: 'answer3',
        label: t({
          id: 'eyes-on-equality.question.answer-no-difference',
          message: 'No difference',
        }),
      },
    ],
    [i18n]
  );

  const handleStepEnter = async (
    evt: OnStepEnterExitArgs<keyof EyesOnEqualityData['equal_treatment_important_hic']>
  ) => {
    if (is.string(evt.data)) {
      setChartData(data.equal_treatment_important_hic[evt.data]);
      setChartLabel(
        t({
          id: 'eyes-on-equality.chart-label',
          message:
            '% in high-income countries who think equal treatment of {minority} is very important',
          values: {
            minority: getMinorityLabel(evt.data),
          },
        })
      );
    }
  };

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.merlot} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="eyes-on-equality.theme">Discrimination</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="eyes-on-equality.tagline">
              <Text type="heading">
                Over the past several decades, many countries have passed laws aimed at enshrining{' '}
                <span>equal rights</span> for all. How have views across generations evolved?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <StoryQuestion
          question={t({
            id: 'eyes-on-equality.question.question',
            message:
              'On average, which generation expresses more concern about the equal treatment of women, racial and ethnic minorities, and religious minorities?',
          })}
          answers={answers}
          onAnswer={handleAnswer}
          selectedAnswer={selectedAnswer}
          response={
            selectedAnswer?.id &&
            t({
              id: 'eyes-on-equality.question.response',
              message: select(selectedAnswer.id, {
                answer1: 'Afraid not!',
                answer2: 'Afraid not!',
                answer3: "You're right!",
              }),
            })
          }
        />

        <CenteredStoryText>
          <Trans id="eyes-on-equality.text-1">
            On average, we find strong, similarly-sized majorities of both young and older people
            supporting <AnnotatedType>equal treatment</AnnotatedType> of each of these groups.
          </Trans>
        </CenteredStoryText>

        <CenteredStoryText>
          <Trans id="eyes-on-equality.text-2">
            In rich countries, things are different. There, young people express{' '}
            <AnnotatedType>greater concern</AnnotatedType> about equal treatment and discrimination
            than older folks.
          </Trans>
        </CenteredStoryText>

        <ScrollRoot>
          <ScrollGraphic>
            <DotMatrix
              data={chartData}
              formatValue={formatValue}
              label={chartLabel}
              getLabel={(side) =>
                ({ left: youngerGeneration(i18n), right: olderGeneration(i18n) }[side])
              }
              getLeft={(d) => d.younger}
              getRight={(d) => d.older}
              getNumberOfCircles={(n) => Math.round(n * 100)}
            />
          </ScrollGraphic>

          <ScrollSteps onStepEnter={handleStepEnter}>
            <ScrollStep data="minority_racial">
              <StoryText>
                <Trans id="eyes-on-equality.text-3">
                  This is true for <AnnotatedType>racial and ethnic minorities,</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
            <ScrollStep data="minority_religious">
              <StoryText>
                <Trans id="eyes-on-equality.text-4">
                  <AnnotatedType>religious minorities,</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
            <ScrollStep data="minority_females">
              <StoryText>
                <Trans id="eyes-on-equality.text-5">
                  <AnnotatedType>females,</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
            <ScrollStep data="minority_lgbtq">
              <StoryText>
                <Trans id="eyes-on-equality.text-6">
                  and <AnnotatedType>LGBTQ+ people.</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
          </ScrollSteps>
        </ScrollRoot>

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          relatedStoryId={relatedStoryId}
          sharing={{
            title: pageTitle,
            url: sharingUrl,
            text: t({
              id: 'eyes-on-equality.sharing.text',
              message:
                'Over the past several decades, many countries have passed laws aimed at enshrining equal rights for all. How have views across generations evolved?',
            }),
          }}
        >
          <Trans id="eyes-on-equality.closing-question">
            What progress can young people achieve in the{' '}
            <AnnotatedType>fight against discrimination</AnnotatedType> in their lifetime?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
